/**
 * Converts a single DOM element into one line of the assistant page outline.
 *
 * Output is compatible with Playwright's aria snapshot format:
 *
 *     role "name" [attr=value]
 *
 * Containers (landmarks, dialogs, tablists, etc.) end with `:` so the outer
 * walker knows to render their children indented below them. Leaf controls
 * (buttons, links, form fields, headings) omit the trailing `:`.
 *
 * State collection (selected, disabled, checked, …) lives in
 * `assistant-page-snapshot-states.ts` and field value / redaction logic in
 * `assistant-page-snapshot-field-value.ts`, keeping this file focused on role
 * → line formatting.
 *
 * References / further reading:
 * - Accessible Name and Description Computation (the algorithm implemented by
 *   `computeAccessibleName`):        https://w3c.github.io/accname/
 * - HTML → ARIA role mapping (the basis for `getRole`):
 *                                    https://www.w3.org/TR/html-aria/
 * - Playwright aria snapshot format (why the output looks the way it does):
 *                                    https://playwright.dev/docs/aria-snapshots
 * - `dom-accessibility-api`, the library this file delegates the hard,
 *   spec-heavy parts to:             https://github.com/eps1lon/dom-accessibility-api
 */
import { computeAccessibleName, getRole } from "dom-accessibility-api"

import {
    fieldValue,
    isPasswordLikeField,
    REDACTED_VALUE,
} from "~utils/helpers/assistant-page-snapshot-field-value"
import { collectStates } from "~utils/helpers/assistant-page-snapshot-states"

const MAX_TEXT_LENGTH = 240

const LEAF_CONTROL_ROLES = new Set([
    "button",
    "link",
    "tab",
    "menuitem",
    "menuitemcheckbox",
    "menuitemradio",
    "option",
    "treeitem",
    "listitem",
])
const FIELD_ROLES = new Set([
    "textbox",
    "searchbox",
    "combobox",
    "checkbox",
    "radio",
    "switch",
    "spinbutton",
    "slider",
])
const LANDMARK_ROLES = new Set([
    "navigation",
    "main",
    "banner",
    "contentinfo",
    "complementary",
    "region",
    "form",
    "search",
])
const CONTAINER_ROLES = new Set([
    "tablist",
    "menu",
    "menubar",
    "listbox",
    "tree",
    "list",
    "table",
    "grid",
    "dialog",
    "alertdialog",
    ...LANDMARK_ROLES,
])
const LIVE_REGION_LEAF_ROLES = new Set(["alert", "status"])

export function describeNode(node: Element): string | null {
    if (isPasswordLikeField(node)) return describePasswordField(node)

    const role = resolveRole(node)
    if (!role) return null

    if (role === "heading") return describeHeading(node, role)
    if (LEAF_CONTROL_ROLES.has(role)) return describeLeafControl(node, role)
    if (FIELD_ROLES.has(role)) return describeField(node, role)
    if (LIVE_REGION_LEAF_ROLES.has(role)) return describeLiveRegion(node, role)
    if (CONTAINER_ROLES.has(role)) return describeContainer(node, role)
    return null
}

export function resolveRole(node: Element): string | null {
    return getRole(node)
}

export function accessibleName(node: Element): string {
    // `computedStyleSupportsPseudoElements: false` avoids JSDOM console noise
    // when computing text from ::before/::after content (we don't need those
    // for an outline, and JSDOM doesn't support them).
    const raw = computeAccessibleName(node, {
        computedStyleSupportsPseudoElements: false,
    })
    return truncate(raw.replace(/\s+/g, " ").trim())
}

export function shortText(node: Element): string {
    const text = (node.textContent ?? "").replace(/\s+/g, " ").trim()
    return truncate(text)
}

function describeHeading(node: Element, role: string): string | null {
    const name = accessibleName(node)
    if (!name) return null
    const level = headingLevel(node)
    return level === null
        ? `${role} "${name}"`
        : `${role} "${name}" [level=${level}]`
}

function describeLeafControl(node: Element, role: string): string | null {
    // `listitem` has nameFrom=author in ARIA, so `computeAccessibleName`
    // returns "" for a plain `<li>Text</li>`. Fall back to the rendered text
    // so list outlines carry their content.
    const name =
        accessibleName(node) || (role === "listitem" ? shortText(node) : "")
    if (!name) return null
    const states = collectStates(node, role)
    return states ? `${role} "${name}" ${states}` : `${role} "${name}"`
}

function describeField(node: Element, role: string): string {
    const name = accessibleName(node) || node.getAttribute("name") || ""
    const states = collectStates(node, role)
    const value = fieldValue(node, role, truncate)

    const nameSegment = name ? ` "${name}"` : ""
    const stateSegment = states ? ` ${states}` : ""
    const valueSegment = value === null ? "" : `: ${value}`
    return `${role}${nameSegment}${stateSegment}${valueSegment}`
}

function describeLiveRegion(node: Element, role: string): string | null {
    // `aria-label`/`aria-labelledby` are the standard way to name a live
    // region that has no inline text, so prefer the accessible name and
    // fall back to the rendered text.
    const text = accessibleName(node) || shortText(node)
    return text ? `${role} "${text}"` : null
}

function describeContainer(node: Element, role: string): string {
    const name = accessibleName(node)
    return name ? `${role} "${name}":` : `${role}:`
}

function describePasswordField(node: Element): string {
    const role = "password"
    const name = accessibleName(node) || node.getAttribute("name") || ""
    const states = collectStates(node, role)
    const nameSegment = name ? ` "${name}"` : ""
    const stateSegment = states ? ` ${states}` : ""
    return `${role}${nameSegment}${stateSegment}: ${REDACTED_VALUE}`
}

function headingLevel(node: Element): number | null {
    const tag = node.tagName
    if (/^H[1-6]$/.test(tag)) return Number(tag.slice(1))
    const explicit = node.getAttribute("aria-level")
    const parsed = explicit ? Number(explicit) : NaN
    // ARIA only allows positive integer heading levels, so reject 0,
    // negatives, and decimals to avoid emitting impossible levels.
    return Number.isInteger(parsed) && parsed >= 1 ? parsed : null
}

function truncate(value: string): string {
    if (value.length <= MAX_TEXT_LENGTH) return value
    return `${value.slice(0, MAX_TEXT_LENGTH - 1)}…`
}
